import cn from 'clsx';
import Link from 'next/link';
import Image from 'next/image';
import {priceHelper} from '@helpers/config-master.mjs';
import autoAnimate from '@formkit/auto-animate';
import {instantMeiliSearch} from '@meilisearch/instant-meilisearch';
import {
  Configure,
  InstantSearch,
  useInfiniteHits,
  useInstantSearch,
  useSearchBox
} from 'react-instantsearch';
import {useEffect, useRef, useState} from 'react';
import {refineMeiliResults} from '@helpers/search/refine-meili-results.mjs';
import {SearchBarMagnifyingGlassIcon} from '@components/widgets/SVGs/SearchBarMagnifyingGlassIcon';
import {useUI} from '@components/widgets/UiProvider';
import {useRouter} from 'next/router';
import {groupSearchResults} from '@helpers/search/search-result-grouping-v2.mjs';
import {formatNumberForDisplay} from '@helpers/common/number.mjs';
import {saveSearchQuery} from '@helpers/search/analytics.mjs';
import {useDeviceTypeWithLoading} from '@helpers/hooks/useWindow';

const {searchClient: meiliClient} = instantMeiliSearch(
  process.env.NEXT_PUBLIC_MEILI_URL,
  process.env.NEXT_PUBLIC_MEILI_SEARCH_KEY,
  {
    placeholderSearch: false
  }
);

const searchClient = {
  ...meiliClient,
  search(requests) {
    if (requests.every(({params}) => !params.query)) {
      return Promise.resolve({
        results: requests.map(() => ({
          hits: [],
          nbHits: 0,
          nbPages: 0,
          page: 0,
          processingTimeMS: 0,
          hitsPerPage: 0,
          exhaustiveNbHits: false,
          query: '',
          params: ''
        }))
      });
    }

    return meiliClient.search(requests);
  }
};

export const MobileSearchV2 = ({setIsOpen, showSearchBoxResults, setShowSearchBoxResults}) => {
  // const sortByField = '';
  const sortByField = ':totalTurn30:desc';
  const indexName = `allProducts${sortByField}`;

  return (
    <InstantSearch
      indexName={indexName}
      searchClient={searchClient}
      routing={false}
      future={{
        preserveSharedStateOnUnmount: false
      }}>
      <SearchWrapper
        indexName={indexName}
        showSearchBoxResults={showSearchBoxResults}
        setShowSearchBoxResults={setShowSearchBoxResults}
        setIsOpen={setIsOpen}
      />
    </InstantSearch>
  );
};

const SearchWrapper = ({indexName, setIsOpen, showSearchBoxResults, setShowSearchBoxResults}) => {
  const [inputValue, setInputValue] = useState('');
  const {closeMobileNav} = useUI();

  const handleCloseNav = () => {
    closeMobileNav();
    setTimeout(() => {
      console.log(
        'MobileSearchV2 handleCloseNav, resetting input value and closing search box results'
      );
      setInputValue('');
      setShowSearchBoxResults(false);
    }, 100);
  };

  return (
    <>
      <Configure analytics={false} hitsPerPage={150} />
      <div className="w-full">
        <div className="max-w-[500px] w-full md:px-4">
          <SearchBar
            indexName={indexName}
            inputValue={inputValue}
            setInputValue={setInputValue}
            setIsOpen={setIsOpen}
            showSearchBoxResults={showSearchBoxResults}
            setShowSearchBoxResults={setShowSearchBoxResults}
          />
        </div>
      </div>
      <SearchResultsWrapper
        indexName={indexName}
        setIsOpen={setIsOpen}
        handleCloseNav={handleCloseNav}
        showSearchBoxResults={showSearchBoxResults}
        setShowSearchBoxResults={setShowSearchBoxResults}
      />
    </>
  );
};

const SearchBar = (props) => {
  const {refine, clear} = useSearchBox(props);

  const {focusOnMobileSearch} = useUI();
  const {isMobile} = useDeviceTypeWithLoading();

  const router = useRouter();
  const isSearchPage = router?.asPath?.includes('/search/products');
  const showSearchBox = !isSearchPage;

  const inputRef = useRef(null);
  const indexName = props?.indexName;

  const setIsOpen = props?.setIsOpen;
  const inputValue = props?.inputValue;
  const setInputValue = props?.setInputValue;

  const setShowSearchBoxResults = props?.setShowSearchBoxResults;

  useEffect(() => {
    if (focusOnMobileSearch === true) {
      inputRef?.current?.focus();
    }
  }, [focusOnMobileSearch]);

  useEffect(() => {
    const handleDebouncedQuery = async (inputValue) => {
      refine(inputValue);
      await saveSearchQuery(inputValue, {
        isMobile: isMobile
      });
    };

    const timerId = setTimeout(() => {
      if (inputValue && inputValue !== '') {
        handleDebouncedQuery(inputValue);
      }
    }, 250);

    if (inputValue === '' || !inputValue) {
      setShowSearchBoxResults(false);
    } else {
      setShowSearchBoxResults(true);
    }

    return () => {
      clearTimeout(timerId);
    };
  }, [inputValue, setShowSearchBoxResults, refine]);

  const handleSubmit = (e) => {
    setIsOpen(false);
    inputRef.current.blur();
  };

  if (!showSearchBox) {
    return null;
  }

  const handleCancel = () => {
    setInputValue('');
    clear();
    setShowSearchBoxResults(false);
  };

  return (
    <form action="/search/products" method="GET" onSubmit={handleSubmit}>
      <div className="px-7 pb-4 border-b-2 top-0 border-black flex items-center gap-4">
        <div className="relative w-full text-gray-600">
          <label className="visually-hidden" htmlFor="drawerSearchBar">
            Search
          </label>
          <input
            ref={inputRef}
            className="text-center text-white font-oswald w-full rounded-md outline-none"
            style={{
              background: '#484B4C',
              border: '1px solid white',
              fontWeight: '400',
              fontSize: '18px',
              letterSpacing: '1px',
              paddingBottom: '1px',
              height: '44px'
            }}
            placeholder="SEARCH"
            type="text"
            name={`${indexName}[query]`}
            id="drawerSearchBar"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
          />
          <SearchBarMagnifyingGlassIcon
            className="absolute right-3 top-1/2 transform -translate-y-1/2"
            size={'26px'}
            color="#FFF"
          />
        </div>
        {inputValue && (
          <div className="text-white text-sm" onClick={handleCancel}>
            Cancel
          </div>
        )}
      </div>
    </form>
  );
};

const SearchResultsWrapper = (props) => {
  const showSearchBoxResults = props?.showSearchBoxResults;
  const handleCloseNav = props?.handleCloseNav;

  const {status, results, setUiState} = useInstantSearch();
  const {hits, showMore, isLastPage} = useInfiniteHits(props);
  const {isMobile} = useDeviceTypeWithLoading();
  const collectionParent = useRef(null);

  const query = results?.query;

  let searchResults = refineMeiliResults(results, hits) || [];
  searchResults = searchResults.slice(0, 20);
  const groupedItems = groupSearchResults(searchResults);
  // console.log('[SearchResultsWrapper][searchResults]:', groupedItems);

  const hasResults = searchResults?.length > 0;
  const noResults = query !== '' && status === 'idle' && searchResults?.length === 0;

  useEffect(() => {
    collectionParent.current && autoAnimate(collectionParent.current);
  }, [collectionParent]);

  const handleClick = async (sku, productUrl) => {
    setUiState({});
    handleCloseNav();
    await saveSearchQuery(query, {
      searchAction: 'clicked-product',
      searchBoxSource: 'mobile-menu',
      clickedSku: sku,
      clickedSkuPath: productUrl ? productUrl : null,
      isMobile: isMobile
    });
  };

  const onViewAllResults = async () => {
    setUiState({});
    handleCloseNav();
    await saveSearchQuery(query, {
      searchAction: 'see-all-results',
      searchBoxSource: 'mobile-menu',
      isMobile: isMobile
    });
  };

  return (
    <div
      className={cn('overflow-y-auto h-full relative z-20 transition-all duration-200', {
        'bg-white pointer-events-auto': showSearchBoxResults,
        'bg-transparent pointer-events-none': !showSearchBoxResults
      })}>
      <div className="absolute inset-0">
        {noResults && (
          <div className="text-center mt-4 italic text-black">{`No results found for "${query}"`}</div>
        )}
        {hasResults && (
          <div className="px-2 py-2 pb-40" ref={collectionParent}>
            {groupedItems?.map((group, index) => {
              const id = group?.id;
              const title = group?.title;
              const handle = group?.handle;
              const groupItems = group?.items;
              if (groupItems.length === 0) return null;

              const key = `collection-results-${id}-${index}`;
              const hasCollection = title && title !== 'Other';
              const isFirstGroup = index === 0;

              return (
                <div key={key} className={`relative mb-4`} tabIndex={index}>
                  {hasCollection && (
                    <div className="text-neutral-700 flex items-center justify-between text-sm leading-4 uppercase font-oswald search-collection-title mb-2">
                      <div className="bg-white px-1 relative z-10">{title}</div>
                      {handle && (
                        <Link
                          href={`/collections${handle}`}
                          onClick={handleCloseNav}
                          className="bg-white px-1 relative z-10 text-xs underline">
                          View All
                        </Link>
                      )}
                    </div>
                  )}
                  {!hasCollection && !isFirstGroup && (
                    <div className="h-0.5 border-b border-b-[#444444] mx-0.5 mb-4"></div>
                  )}

                  <SearchResultsBoxMobileCollection items={groupItems} handleClick={handleClick} />
                </div>
              );
            })}

            <div className="flex justify-center">
              <Link
                href={`/search/products?allProducts%3AtotalTurn30%3Adesc%5Bquery%5D=${results?.query}`}
                className="text-white text-xs bg-neutral-700 shadow px-4 py-1 rounded uppercase"
                onClick={onViewAllResults}>
                View All Results
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const SearchResultsBoxMobileCollection = ({items, handleClick}) => {
  return (
    <div className="grid grid-cols-2 gap-2">
      {items.map((product, index) => {
        const isFirstTwo = index < 2;
        return (
          <div
            key={`search-result-${product?.sku}-${index}`}
            className="border border-neutral-300 rounded">
            <SearchResultsBoxMobileItem
              product={product}
              handleClick={handleClick}
              highlightSales={isFirstTwo}
            />
          </div>
        );
      })}
    </div>
  );
};

const SearchResultsBoxMobileItem = ({product, handleClick, highlightSales}) => {
  return (
    <div
      onClick={(e) => {
        handleClick(product?.sku, product?.url);
      }}>
      <Link
        className={'flex flex-col justify-between h-full cursor-pointer text-base py-1 pr-2 pl-1'}
        href={`/products/${product?.slug}`}>
        <div className="flex gap-0.5">
          <div className="w-[50px] h-[50px] flex-shrink-0">
            <Image
              width={50}
              height={50}
              alt="PriceHelper"
              src={product?.imageUrl ?? priceHelper.placeholder?.mobile?.product}
            />
          </div>
          <div>
            <div className="text-black text-xs font-oswald line-clamp-3 leading-4">
              {product.name}
            </div>
            {product?.totalTurn30 > 0 && (
              <div className={`leading-3 tracking-tight whitespace-pre text-[10px]`}>
                {product?.isBestSeller && '🔥'}
                <span
                  className={cn(`text-black`, {
                    'text-[13px] best-seller-bg': highlightSales
                  })}>
                  {formatNumberForDisplay(product?.totalTurn30)}
                </span>{' '}
                sold this month
              </div>
            )}
            <div className="text-gray-400 text-[10px] leading-3 tracking-tight line-clamp-1">
              {product?.displaySku || product?.sku}
            </div>
          </div>
        </div>
      </Link>
    </div>
  );
};
